import { useI18n } from '@ecom/i18n';
import { resolveErrorMessageKey } from '@ecom/i18n/errors';
import {
  Button,
  EmptyState,
  Field,
  Input,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeaderCell,
  TableRow,
} from '@ecom/ui-kit';
import { type ReactElement, useState } from 'react';

import {
  downloadAuditExport,
  EMPTY_FILTERS,
  useAuditJournalQuery,
  type AuditEntry,
  type AuditFilters,
} from '../api';

import { TableSkeleton } from '@/components/ui/skeletons/TableSkeleton';
import { useToast } from '@/components/ui/toast/ToastProvider';
import { useSession } from '@/lib/session';

/** The details the database allowed to leave, as short `key: value` text (never raw JSON in the middle of a table). */
function detailsText(details: Readonly<Record<string, unknown>>): string {
  return Object.entries(details)
    .map(([key, value]) => `${key}: ${typeof value === 'string' ? value : JSON.stringify(value)}`)
    .join(' · ');
}

function actorText(entry: AuditEntry, anonymous: string): string {
  return entry.actorEmail ?? entry.actorUserId ?? anonymous;
}

/**
 * The global audit journal, read only. Filters narrow it (an action PREFIX, a target, an actor, an organisation, a period); "older
 * entries" follows the opaque cursor of the API; the export is the same filters as a CSV, and is itself recorded in the journal. The
 * entries show only the details the database allow-lists: nothing here can reveal a secret that a writer put in a snapshot.
 */
export function AuditJournal({ reason }: { readonly reason: string }): ReactElement {
  const { t } = useI18n();
  const toast = useToast();
  const { accessToken } = useSession();
  const [draft, setDraft] = useState<AuditFilters>(EMPTY_FILTERS);
  const [applied, setApplied] = useState<AuditFilters>(EMPTY_FILTERS);
  const [exporting, setExporting] = useState(false);

  const query = useAuditJournalQuery(reason, applied);
  const entries = query.data?.pages.flatMap((page) => page.entries) ?? [];

  function set(key: keyof AuditFilters, value: string): void {
    setDraft((current) => ({ ...current, [key]: value }));
  }

  async function onExport(): Promise<void> {
    setExporting(true);
    try {
      const blob = await downloadAuditExport(accessToken, reason, applied);
      const url = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `audit-${new Date().toISOString().slice(0, 10)}.csv`;
      document.body.appendChild(link);
      link.click();
      link.remove();
      URL.revokeObjectURL(url);
      toast.success(t('admin.security.audit.exported'));
    } catch (error) {
      toast.error(t(resolveErrorMessageKey(error)));
    } finally {
      setExporting(false);
    }
  }

  return (
    <div className="flex flex-col gap-4">
      <form
        className="grid gap-3 rounded-xl border border-line bg-surface p-4 sm:grid-cols-2 lg:grid-cols-3"
        onSubmit={(event) => {
          event.preventDefault();
          setApplied(draft);
        }}
      >
        <Field label={t('admin.security.audit.filterAction')}>
          {(bind) => (
            <Input
              {...bind}
              value={draft.action}
              placeholder={t('admin.security.audit.filterActionPlaceholder')}
              autoComplete="off"
              onChange={(event) => {
                set('action', event.target.value);
              }}
            />
          )}
        </Field>
        <Field label={t('admin.security.audit.filterTarget')}>
          {(bind) => (
            <Input
              {...bind}
              value={draft.target}
              autoComplete="off"
              onChange={(event) => {
                set('target', event.target.value);
              }}
            />
          )}
        </Field>
        <Field label={t('admin.security.audit.filterActor')}>
          {(bind) => (
            <Input
              {...bind}
              value={draft.actor}
              autoComplete="off"
              onChange={(event) => {
                set('actor', event.target.value);
              }}
            />
          )}
        </Field>
        <Field label={t('admin.security.audit.filterTenant')}>
          {(bind) => (
            <Input
              {...bind}
              value={draft.tenant}
              autoComplete="off"
              onChange={(event) => {
                set('tenant', event.target.value);
              }}
            />
          )}
        </Field>
        <Field label={t('admin.security.audit.filterFrom')}>
          {(bind) => (
            <Input
              {...bind}
              type="date"
              value={draft.from}
              onChange={(event) => {
                set('from', event.target.value);
              }}
            />
          )}
        </Field>
        <Field label={t('admin.security.audit.filterTo')}>
          {(bind) => (
            <Input
              {...bind}
              type="date"
              value={draft.to}
              onChange={(event) => {
                set('to', event.target.value);
              }}
            />
          )}
        </Field>
        <div className="flex items-end gap-2 sm:col-span-2 lg:col-span-3">
          <Button type="submit">{t('admin.security.audit.apply')}</Button>
          <Button
            type="button"
            variant="secondary"
            loading={exporting}
            onClick={() => void onExport()}
          >
            {t('admin.security.audit.export')}
          </Button>
          <p className="text-xs text-inkFaint">{t('admin.security.audit.exportHint')}</p>
        </div>
      </form>

      {query.isPending ? (
        <TableSkeleton columns={6} />
      ) : query.isError ? (
        <EmptyState title={t('admin.security.loadError')} />
      ) : entries.length === 0 ? (
        <EmptyState title={t('admin.security.audit.empty')} />
      ) : (
        <>
          <Table caption={t('admin.security.audit.caption')}>
            <TableHead>
              <TableRow>
                <TableHeaderCell>{t('admin.security.audit.colWhen')}</TableHeaderCell>
                <TableHeaderCell>{t('admin.security.audit.colActor')}</TableHeaderCell>
                <TableHeaderCell>{t('admin.security.audit.colAction')}</TableHeaderCell>
                <TableHeaderCell>{t('admin.security.audit.colTarget')}</TableHeaderCell>
                <TableHeaderCell>{t('admin.security.audit.colIp')}</TableHeaderCell>
                <TableHeaderCell>{t('admin.security.audit.colDetails')}</TableHeaderCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {entries.map((entry) => (
                <TableRow key={entry.id}>
                  <TableCell>
                    <span className="text-xs text-inkFaint">
                      {new Date(entry.createdAt).toLocaleString('fr-TN')}
                    </span>
                  </TableCell>
                  <TableCell>
                    <span className="break-all text-xs text-ink">
                      {actorText(entry, t('admin.security.audit.anonymous'))}
                    </span>
                    <p className="text-xs text-inkFaint">{entry.actorRole}</p>
                  </TableCell>
                  <TableCell>
                    <span className="break-all font-mono text-xs text-ink">{entry.action}</span>
                  </TableCell>
                  <TableCell>
                    <span className="break-all font-mono text-xs text-inkSoft">
                      {entry.targetId ?? '—'}
                    </span>
                    {entry.tenantId !== null ? (
                      <p className="break-all font-mono text-xs text-inkFaint">{entry.tenantId}</p>
                    ) : null}
                  </TableCell>
                  <TableCell>
                    <span className="font-mono text-xs text-inkFaint">
                      {entry.ipAddress ?? '—'}
                    </span>
                  </TableCell>
                  <TableCell>
                    <span className="break-words text-xs text-inkSoft">
                      {detailsText(entry.details) || '—'}
                    </span>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
          {query.hasNextPage ? (
            <div className="flex justify-center">
              <Button
                variant="secondary"
                loading={query.isFetchingNextPage}
                onClick={() => {
                  void query.fetchNextPage();
                }}
              >
                {t('admin.security.audit.loadMore')}
              </Button>
            </div>
          ) : null}
        </>
      )}
    </div>
  );
}
